import { useState, useRef, useEffect, ChangeEvent, CSSProperties } from "react";
import Colors from "../../constants/Colors";
import { FaPen, FaTrash } from "react-icons/fa";
import blob from "../../services/blob";
import { useAuth } from "../../context/AuthContext";
import utils from "../../helpers/utils";
import Load from "./Load";

interface ImageInputProps {
  onChange?: (value: string | string[]) => void;
  value?: string | string[];
  label?: string;
  id?: string;
  name?: string;
  className?: string;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  colorLabel?: string;
  classNameImage?: string;
  background?: string;
  objectFit?: CSSProperties["objectFit"];
  onError?: string;
  multiple?: boolean;
}

function ImageInput({
  onChange,
  value,
  label,
  id,
  name,
  className,
  placeholder,
  required = false,
  maxLength,
  colorLabel = Colors.background,
  classNameImage = "",
  background = Colors.gray3,
  objectFit = "cover",
  onError,
  multiple = false,
}: ImageInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [myValue, setMyValue] = useState<string | string[] | undefined>(value);
  const [loading, setLoading] = useState(false);

  const { getUser } = useAuth();
  const handleImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    setMyValue(value);
  }, [value]);

  const getBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      try {
        setLoading(true);
        const user = getUser();

        if (user) {
          if (multiple) {
            const newPaths: string[] = [];
            for (let i = 0; i < files.length; i++) {
              const file = files[i];
              const base64String = await getBase64(file);
              const base64Data = `${base64String}`.split(",")[1];
              const uploadResult = await blob.uploadImage(
                String(user.id),
                base64Data,
                file.name
              );
              
              if (uploadResult && typeof uploadResult === 'object' && uploadResult.url) {
                // Usa a URL pré-assinada retornada pelo backend
                newPaths.push(uploadResult.url);
              } else if (uploadResult && typeof uploadResult === 'object' && uploadResult.path) {
                // Fallback para o path se a URL não estiver disponível
                newPaths.push(uploadResult.path);
              } else if (uploadResult) {
                // Fallback para compatibilidade com versões antigas
                newPaths.push(`/${user.id}/${uploadResult}`);
              }
            }

            const currentValues = Array.isArray(myValue)
              ? (myValue as string[])
              : myValue
              ? [myValue as string]
              : [];
            const updatedValues = [...currentValues, ...newPaths];

            if (onChange) {
              onChange(updatedValues);
              setMyValue(updatedValues);
            }
          } else {
            const file = files[0];
            const base64String = await getBase64(file);
            const base64Data = `${base64String}`.split(",")[1];

            const uploadResult = await blob.uploadImage(
              String(user.id),
              base64Data,
              file.name
            );

            let resultValue: string;
            if (uploadResult && typeof uploadResult === 'object' && uploadResult.url) {
              // Usa a URL pré-assinada retornada pelo backend
              resultValue = uploadResult.url;
            } else if (uploadResult && typeof uploadResult === 'object' && uploadResult.path) {
              // Fallback para o path se a URL não estiver disponível
              resultValue = uploadResult.path;
            } else if (uploadResult) {
              // Fallback para compatibilidade com versões antigas
              resultValue = `/${user.id}/${uploadResult}`;
            } else {
              resultValue = '';
            }

            if (onChange && resultValue) {
              onChange(resultValue);
              setMyValue(resultValue);
            }
          }
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error uploading file:", error);
      }
    }
  };

  const handleRemove = (index: number) => {
    if (multiple && Array.isArray(myValue)) {
      const newValues = myValue.filter((_, i) => i !== index);
      if (onChange) {
        onChange(newValues);
        setMyValue(newValues);
      }
    }
  };

  if (multiple) {
    return (
      <div className={"mb-4 " + className}>
        <label
          htmlFor={id}
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
        >
          {label}
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.isArray(myValue) &&
            myValue.map((path, index) => (
              <div key={index} className="relative h-[150px] group">
                <img
                  src={utils.getUriImage(path)}
                  className={
                    "w-full h-full rounded-md border border-gray-200 dark:border-gray-800 object-cover"
                  }
                  alt={`img-${index}`}
                />
                <div
                  className="absolute top-2 right-2 p-2 bg-red-500 rounded-full shadow-md cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleRemove(index)}
                >
                  <FaTrash className="text-white" size={14} />
                </div>
              </div>
            ))}
          <div
            className="flex h-[150px] w-full cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-gray-300 hover:border-brand-500 dark:border-gray-700"
            onClick={handleImageClick}
          >
            {loading ? (
              <Load />
            ) : (
              <>
                <FaPen className="mb-2 text-gray-400" size={24} />
                <span className="text-sm text-gray-500">Adicionar</span>
              </>
            )}
          </div>
        </div>
        <input
          id={id}
          name={name}
          disabled={loading}
          type="file"
          multiple
          maxLength={maxLength}
          onChange={handleFileChange}
          accept=".jpg,.png"
          ref={fileInputRef}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className={"mb-4 " + className}>
      <label
        htmlFor={id}
        className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
      >
        {label}
      </label>
      <div className="relative w-full cursor-pointer flex flex-col md:flex-row lg:flex-row gap-4 items-center">
        <img
          src={
            !loading
              ? utils.getUriImage((myValue as string) ?? "")
              : "https://upload.wikimedia.org/wikipedia/commons/c/ca/1x1.png"
          }
          onError={() =>
            setMyValue(
              onError ??
                "https://upload.wikimedia.org/wikipedia/commons/c/ca/1x1.png"
            )
          }
          className={
            "w-full md:w-1/2 lg:w-1/2 rounded-md h-[250px] border border-gray-200 dark:border-gray-800 " +
            classNameImage
          }
          onClick={handleImageClick}
          alt=""
          style={{
            zIndex: 0,
            objectFit: objectFit,
            background: background,
          }}
        />
        <div
          className="absolute top-2 left-2 p-2 bg-brand-500 rounded-full shadow-xl cursor-pointer hover:bg-brand-600 transition-colors"
          style={{ zIndex: 2 }}
          onClick={handleImageClick}
        >
          {loading ? <Load /> : <FaPen className="text-white" size={18} />}
        </div>
        <input
          id={id}
          name={name}
          disabled={loading}
          type="file"
          maxLength={maxLength}
          onChange={handleFileChange}
          required={required}
          accept=".jpg,.png"
          ref={fileInputRef}
          className="hidden"
          placeholder={placeholder}
        />
      </div>
    </div>
  );
}

export default ImageInput;
